import { Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IBreadcrumbsAndMenu, IGlobalSetting } from "../../../types/global";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import GlobalSettingTable from "../../../components/Table/Setting/Global";
import { useModal } from "../../../hooks/use-modal";
import GlobalSettingModal from "../../../components/UI/Modal/GlobalSettingModal";
import { useState } from "react";
import { GraphQLClient } from "graphql-request";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";
import { useSnackbar } from "notistack";
import ConfirmationModal from "../../../components/UI/Modal/ConfirmationModal";
import { useSearchParams } from "react-router-dom";
import {
  GeneralModelType,
  SalesTypeFindUniqueQuery,
  SalesTypesFindAllQuery,
  useSalesTypeCreateMutation,
  useSalesTypeDeleteMutation,
  useSalesTypeFindUniqueQuery,
  useSalesTypeUpdateMutation,
  useSalesTypesFindAllQuery,
} from "../../../generated/general";
import { useStateContext } from "../../../contexts/auth-context";

const SalesTypeSetting = () => {
  const { t } = useTranslation();
  const [deletingId, setDeletingId] = useState<number>();
  const {
    state: { permissions },
  } = useStateContext();

  let [searchParams, setSearchParams] = useSearchParams();

  const id = searchParams.get("id");

  const setSearchParamsHandler = (key: string, value: string) => {
    searchParams.set(key, value);
    setSearchParams(searchParams);
  };

  const removeSearchParamsHandler = (key: string) => {
    searchParams.delete(key);
    setSearchParams(searchParams);
  };

  const { modal, openModalHandler, closeModalHandler } = useModal(
    undefined,
    () => removeSearchParamsHandler("id")
  );

  const { enqueueSnackbar } = useSnackbar();

  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("setting.index"),
      to: "/setting",
    },
    {
      name: t("setting.sales.index"),
      to: "/setting/sales",
    },
    {
      name: t("setting.sales.type"),
    },
  ];

  const graphQLClientGeneral: GraphQLClient =
    createGraphQLClientWithMiddleware("general");

  const { data: rowData, refetch: refetchRowData } =
    useSalesTypesFindAllQuery<SalesTypesFindAllQuery>(graphQLClientGeneral);

  const { data, isLoading, isSuccess } =
    useSalesTypeFindUniqueQuery<SalesTypeFindUniqueQuery>(
      graphQLClientGeneral,
      {
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      },
      {
        enabled: Boolean(id),
      }
    );

  const { mutateAsync: createSaleType } =
    useSalesTypeCreateMutation<Error>(graphQLClientGeneral);

  const { mutateAsync: updateSaleType } =
    useSalesTypeUpdateMutation<Error>(graphQLClientGeneral);

  const { mutateAsync: deleteSaleType } =
    useSalesTypeDeleteMutation<Error>(graphQLClientGeneral);

  const onAddHandler = async (data: IGlobalSetting) => {
    try {
      await createSaleType({
        data,
      });
      enqueueSnackbar(`เพิ่ม${t("setting.sales.type")}สำเร็จ`, {
        variant: "success",
      });
      await refetchRowData();
    } catch (err) {
      enqueueSnackbar(`เพิ่ม${t("setting.sales.type")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const onEditHandler = (id?: number) => {
    if (id) {
      setSearchParamsHandler("id", id.toString());
      openModalHandler();
    }
  };

  const onUpdateHandler = async (data: IGlobalSetting) => {
    try {
      const { id, ...otherData } = data;
      await updateSaleType({
        uniqueInput: {
          id: id,
        },
        data: otherData,
      });
      enqueueSnackbar(`แก้ไข${t("setting.sales.type")}สำเร็จ`, {
        variant: "success",
      });
      await refetchRowData();
    } catch (err) {
      enqueueSnackbar(`แก้ไข${t("setting.sales.type")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const onDeleteHandler = (id?: number) => {
    if (id) {
      setDeletingId(id);
      openConfirmationHandler();
    }
  };

  const deleteActionHandler = async () => {
    try {
      await deleteSaleType({
        uniqueInput: {
          id: deletingId,
        },
      });
      setDeletingId(undefined);
      enqueueSnackbar(`ลบ${t("setting.sales.type")}สำเร็จ`, {
        variant: "success",
      });
      await refetchRowData();
    } catch (err) {
      enqueueSnackbar(`ลบ${t("setting.sales.type")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const {
    modal: confirmation,
    openModalHandler: openConfirmationHandler,
    closeModalHandler: closeConfirmationHandler,
    submitModalHandler: submitConfirmationHandler,
  } = useModal(deleteActionHandler);
  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ display: "flex", justifyContent: "space-between", my: 3 }}>
        <Typography variant="h5">{t("setting.sales.type")}</Typography>
        {permissions?.sales_setting?.create && (
          <CustomizedButton
            title={t("button.add") + t("setting.sales.type")}
            variant="contained"
            onClick={openModalHandler}
          />
        )}
      </Box>
      <GlobalSettingTable
        data={rowData?.SalesTypesFindAll || []}
        onEditHandler={onEditHandler}
        onDeleteHandler={onDeleteHandler}
        tableType="sales_setting"
      />
      <GlobalSettingModal
        open={modal}
        closeModalHandler={closeModalHandler}
        onAddHandler={onAddHandler}
        onUpdateHandler={onUpdateHandler}
        data={data?.SalesTypeFindUnique}
        isLoading={isLoading}
        isSuccess={isSuccess}
        type={GeneralModelType.SalesType}
      />
      <ConfirmationModal
        title={`ยืนยันการลบ${t("setting.sales.type")}`}
        open={confirmation}
        handleClose={closeConfirmationHandler}
        action={submitConfirmationHandler}
      />
    </>
  );
};

export default SalesTypeSetting;
